import {useCallback, useEffect, useRef, useState} from "react";
import {useLocation} from "react-router";
import {ACTION_FEEDBACK_EVENTS, beginActionFeedback, completeActionFeedbackById, consumePersistedActionFeedbackSuccess, prepareActionFeedback, resetActionFeedbackById,} from "../utils/action-feedback.js";

const SUCCESS_TIMEOUT = 1500;

export const useActionFeedback = ({type, actionKey = null, externalLoading = false, onSuccess, receivePersistedSuccess = false}) => {
    const location = useLocation();
    const [status, setStatus] = useState("idle");
    const [successMessage, setSuccessMessage] = useState(null);
    const [isPrepared, setIsPrepared] = useState(false);
    const actionIdRef = useRef(null);
    const externalLoadingRef = useRef(externalLoading);
    const timeoutRef = useRef(null);
    const onSuccessRef = useRef(onSuccess);

    useEffect(() => {
        onSuccessRef.current = onSuccess;
    }, [onSuccess]);

    const displaySuccess = useCallback((message, notifySuccess) => {
        clearTimeout(timeoutRef.current);
        if (notifySuccess) onSuccessRef.current?.(message);
        setStatus("success");
        setSuccessMessage(message ?? null);
        setIsPrepared(false);
        timeoutRef.current = setTimeout(() => {
            setStatus("idle");
            setSuccessMessage(null);
        }, SUCCESS_TIMEOUT);
    }, []);

    const reset = useCallback(() => {
        const actionId = actionIdRef.current;
        actionIdRef.current = null;
        clearTimeout(timeoutRef.current);
        if (actionId) resetActionFeedbackById(actionId);
        setStatus("idle");
        setSuccessMessage(null);
        setIsPrepared(false);
    }, []);

    const succeed = useCallback((message) => {
        if (!actionIdRef.current) return;

        actionIdRef.current = null;
        displaySuccess(message, true);
    }, [displaySuccess]);

    const showPersistedSuccess = useCallback((message) => {
        const actionId = actionIdRef.current;
        actionIdRef.current = null;
        if (actionId) resetActionFeedbackById(actionId);

        displaySuccess(message, false);
    }, [displaySuccess]);

    const start = useCallback(() => {
        actionIdRef.current = beginActionFeedback(type, actionKey);
        clearTimeout(timeoutRef.current);
        setSuccessMessage(null);
        setStatus("loading");
        setIsPrepared(false);
    }, [actionKey, type]);

    const prepare = useCallback(() => {
        actionIdRef.current = prepareActionFeedback(type, actionKey);
        clearTimeout(timeoutRef.current);
        setSuccessMessage(null);
        setStatus("idle");
        setIsPrepared(true);
    }, [actionKey, type]);

    useEffect(() => {
        if (!type) return undefined;

        const handleFeedback = (event) => {
            if (event.detail?.id !== actionIdRef.current) return;

            if (event.type === ACTION_FEEDBACK_EVENTS.success) succeed(event.detail?.message);
            else if (event.type === ACTION_FEEDBACK_EVENTS.confirmed) {
                setStatus("loading");
                setIsPrepared(false);
            }
            else reset();
        };

        window.addEventListener(ACTION_FEEDBACK_EVENTS.success, handleFeedback);
        window.addEventListener(ACTION_FEEDBACK_EVENTS.confirmed, handleFeedback);
        window.addEventListener(ACTION_FEEDBACK_EVENTS.error, handleFeedback);
        window.addEventListener(ACTION_FEEDBACK_EVENTS.cancel, handleFeedback);

        return () => {
            window.removeEventListener(ACTION_FEEDBACK_EVENTS.success, handleFeedback);
            window.removeEventListener(ACTION_FEEDBACK_EVENTS.confirmed, handleFeedback);
            window.removeEventListener(ACTION_FEEDBACK_EVENTS.error, handleFeedback);
            window.removeEventListener(ACTION_FEEDBACK_EVENTS.cancel, handleFeedback);
            clearTimeout(timeoutRef.current);
            if (actionIdRef.current) resetActionFeedbackById(actionIdRef.current);
        };
    }, [reset, succeed, type]);

    useEffect(() => {
        if (!type || !receivePersistedSuccess) return;

        const message = consumePersistedActionFeedbackSuccess(type, actionKey);
        if (message) showPersistedSuccess(message);
    }, [actionKey, location.hash, location.pathname, location.search, location.key, receivePersistedSuccess, showPersistedSuccess, type]);

    useEffect(() => {
        if (!type) return;

        const wasLoading = externalLoadingRef.current;
        externalLoadingRef.current = externalLoading;

        if (!actionIdRef.current || wasLoading === externalLoading) return;
        if (externalLoading) {
            setStatus("loading");
            setIsPrepared(false);
        }
        else if (!completeActionFeedbackById(actionIdRef.current)) reset();
    }, [externalLoading, reset]);

    return {status, successMessage, start, prepare, reset, isPrepared};
};